import { isDevelopment, isNonProduction } from '@/utils/environmentByHostname';

import type {
    AppLoadedEvent,
    ButtonClickedEvent,
    ComparisonUsedEvent,
    DomainEvent,
    ErrorEncounteredEvent,
    ExportTriggeredEvent,
    FeatureActionUsedEvent,
    PageLeftEvent,
    PageViewedEvent,
    PenSwitchedEvent,
    PenSwitchRenderedEvent,
    QuickSearchOpenedEvent,
    QuickSearchResultSelectedEvent,
    ReportCreatedEvent,
    ReportDownloadedEvent,
    SessionEndedEvent,
    SessionStartedEvent,
} from './domainEvents';
import { IMPERSONATION_FLAG_KEYS } from './impersonationFlags';
import { resetPageviewGateForTesting } from './pageviewGate';
import type { AnalyticsAdapter, AnalyticsContextSnapshot, IdentifyUserPayload } from './types';

type FallbackContextGetter = () => AnalyticsContextSnapshot;

type ContextChannel = 'route' | 'session';

const MAX_BUFFERED_EVENTS = 100;

let adapter: AnalyticsAdapter | null = null;
let contextChannels: Record<ContextChannel, AnalyticsContextSnapshot> = {
    route: {},
    session: {},
};
const routeContextListeners = new Set<() => void>();
let fallbackContextGetter: FallbackContextGetter | null = null;
let missingAdapterWarned = false;
let eventBuffer: DomainEvent[] = [];

function warnBufferOverflowOnce(): void {
    if (!isDevelopment() || missingAdapterWarned) {
        return;
    }

    missingAdapterWarned = true;
    console.warn(
        `[analytics] Event buffer exceeded ${MAX_BUFFERED_EVENTS} entries — dropping oldest events.`
    );
}

function warnMissingAdapterOnce(): void {
    if (!isDevelopment() || missingAdapterWarned) {
        return;
    }

    missingAdapterWarned = true;
    console.warn('[analytics] No adapter registered — event was not captured.');
}

function mergeChannels(): AnalyticsContextSnapshot {
    return {
        ...contextChannels.route,
        ...contextChannels.session,
    };
}

function resolveContext(): AnalyticsContextSnapshot {
    const merged = mergeChannels();
    if (Object.keys(merged).length > 0) {
        return merged;
    }

    return fallbackContextGetter?.() ?? {};
}

function isInternalContext(context: AnalyticsContextSnapshot): boolean {
    return context.navigation_level === 'internal' || !!context.page?.startsWith('/internal');
}

function mirrorToConsole(label: string, details?: unknown): void {
    if (!isNonProduction()) {
        return;
    }

    if (details === undefined) {
        console.log(`[analytics] ${label}`);
        return;
    }

    console.log(`[analytics] ${label}`, details);
}

function captureWithMirror(event: DomainEvent): void {
    if (!adapter) {
        return;
    }

    const context = resolveContext();
    if (event.type === 'buttonClicked' && isInternalContext(context)) {
        return;
    }

    mirrorToConsole(event.type, { event, context });
    adapter.capture(event, context);
}

function dispatch(event: DomainEvent): void {
    if (event.type === 'buttonClicked' && isInternalContext(resolveContext())) {
        return;
    }

    if (!adapter) {
        if (eventBuffer.length >= MAX_BUFFERED_EVENTS) {
            eventBuffer.shift();
            warnBufferOverflowOnce();
        }
        eventBuffer.push(event);
        return;
    }

    captureWithMirror(event);
}

function flushBufferedEvents(): void {
    if (!adapter || eventBuffer.length === 0) {
        return;
    }

    const pending = eventBuffer;
    eventBuffer = [];

    for (const event of pending) {
        captureWithMirror(event);
    }
}

export const analytics = {
    setAdapter(nextAdapter: AnalyticsAdapter | null): void {
        adapter = nextAdapter;
        if (nextAdapter) {
            missingAdapterWarned = false;
            flushBufferedEvents();
        }
    },

    /**
     * Replaces the `route` channel of the shared context. Called on every
     * route change; owns route-scoped keys (client_id, cage_id, page, …).
     * Channels are merged on read — session-scoped context is preserved.
     */
    setRouteContext(snapshot: AnalyticsContextSnapshot): void {
        contextChannels.route = snapshot;
        for (const listener of routeContextListeners) {
            listener();
        }
    },

    /** The pen currently stored on the route channel, which lags a child effect. */
    routeCageId(): number | undefined {
        return contextChannels.route.cage_id;
    },

    /**
     * Notified after `setRouteContext`. Used so a pen-switch measurement can wait
     * until the route channel names the destination pen.
     */
    subscribeRouteContext(listener: () => void): () => void {
        routeContextListeners.add(listener);
        return () => {
            routeContextListeners.delete(listener);
        };
    },

    /**
     * Replaces the `session` channel of the shared context. Reserved for
     * non-route enrichment (experiment variant, tenant tier, …). Channels
     * are expected to own disjoint keys.
     */
    setSessionContext(snapshot: AnalyticsContextSnapshot): void {
        contextChannels.session = snapshot;
    },

    setFallbackContextGetter(getter: FallbackContextGetter | null): void {
        fallbackContextGetter = getter;
    },

    resetForTesting(): void {
        adapter = null;
        contextChannels = { route: {}, session: {} };
        fallbackContextGetter = null;
        missingAdapterWarned = false;
        eventBuffer = [];
        routeContextListeners.clear();
        resetPageviewGateForTesting();
    },

    /**
     * Identity-management operation, not a domain event. Bypasses context
     * enrichment and forwards directly to the adapter. Called from the auth
     * layer to associate the current session with a user.
     */
    identifyUser(payload: IdentifyUserPayload): void {
        if (!adapter?.identifyUser) {
            warnMissingAdapterOnce();
            return;
        }

        mirrorToConsole('identify', payload);
        adapter.identifyUser(payload);
    },

    /**
     * Identity-management operation, not a domain event. Clears the adapter's
     * user association on logout. Bypasses context enrichment.
     */
    reset(): void {
        if (!adapter?.reset) {
            warnMissingAdapterOnce();
            return;
        }

        mirrorToConsole('reset');
        adapter.clearSessionFlags?.([...IMPERSONATION_FLAG_KEYS]);
        adapter.reset();
    },

    setSessionFlags(flags: Record<string, unknown>): void {
        if (!adapter?.setSessionFlags) {
            warnMissingAdapterOnce();
            return;
        }

        mirrorToConsole('setSessionFlags', flags);
        adapter.setSessionFlags(flags);
    },

    clearSessionFlags(keys: string[]): void {
        if (!adapter?.clearSessionFlags) {
            warnMissingAdapterOnce();
            return;
        }

        mirrorToConsole('clearSessionFlags', keys);
        adapter.clearSessionFlags(keys);
    },

    reportDownloaded(payload: Omit<ReportDownloadedEvent, 'type'>): void {
        dispatch({ type: 'reportDownloaded', ...payload });
    },

    reportCreated(payload: Omit<ReportCreatedEvent, 'type'>): void {
        dispatch({ type: 'reportCreated', ...payload });
    },

    exportTriggered(payload: Omit<ExportTriggeredEvent, 'type'>): void {
        dispatch({ type: 'exportTriggered', ...payload });
    },

    comparisonUsed(payload: Omit<ComparisonUsedEvent, 'type'>): void {
        dispatch({ type: 'comparisonUsed', ...payload });
    },

    featureActionUsed(payload: Omit<FeatureActionUsedEvent, 'type'>): void {
        dispatch({ type: 'featureActionUsed', ...payload });
    },

    errorEncountered(payload: Omit<ErrorEncounteredEvent, 'type'>): void {
        dispatch({ type: 'errorEncountered', ...payload });
    },

    quickSearchOpened(payload: Omit<QuickSearchOpenedEvent, 'type'>): void {
        dispatch({ type: 'quickSearchOpened', ...payload });
    },

    quickSearchResultSelected(payload: Omit<QuickSearchResultSelectedEvent, 'type'>): void {
        dispatch({ type: 'quickSearchResultSelected', ...payload });
    },

    /** Password sign-in only. Session restore uses `appLoaded`. */
    loggedIn(): void {
        dispatch({ type: 'loggedIn' });
    },

    appLoaded(payload: Omit<AppLoadedEvent, 'type'>): void {
        dispatch({ type: 'appLoaded', ...payload });
    },

    penSwitched(payload: Omit<PenSwitchedEvent, 'type'>): void {
        dispatch({ type: 'penSwitched', ...payload });
    },

    /**
     * Dashboard overview painted after a next/previous step.
     * Separate from `penSwitched`: the click does not yet know the duration.
     */
    penSwitchRendered(payload: Omit<PenSwitchRenderedEvent, 'type'>): void {
        dispatch({ type: 'penSwitchRendered', ...payload });
    },

    pageViewed(payload: Omit<PageViewedEvent, 'type'>): void {
        dispatch({ type: 'pageViewed', ...payload });
    },

    pageLeft(payload: Omit<PageLeftEvent, 'type'>): void {
        dispatch({ type: 'pageLeft', ...payload });
    },

    sessionStarted(payload: Omit<SessionStartedEvent, 'type'>): void {
        dispatch({ type: 'sessionStarted', ...payload });
    },

    sessionEnded(payload: Omit<SessionEndedEvent, 'type'>): void {
        dispatch({ type: 'sessionEnded', ...payload });
    },

    buttonClicked(payload: Omit<ButtonClickedEvent, 'type'>): void {
        dispatch({ type: 'buttonClicked', ...payload });
    },
};
